import React, {useEffect, useMemo, useReducer, useState} from 'react'
import {connect} from 'react-redux'
import PropTypes from "prop-types";
import Form from "components/Form";
import Loading from "components/Loading";
import BasicData from "./components/BasicData";
import PurchaserData from "./components/PurchaserData";
import FormNavigator from "../components/FormNavigator";
import {tenderResultNoticeStepsOrder} from "./resultNoticeSteps";
import {tenderResultNoticeValidationCast} from "./resultNoticeValidationCast";
import tenderResultNoticeValidation from "./resultNoticeValidation";
import {Collapse, Typography} from "@material-ui/core";
import RealizationTypeData from "./components/RealizationTypeData";
import OrderObjectData from "./components/OrderObjectData";
import TenderCompletionData from "./components/TenderCompletionData";
import AdditionalInfoData from "./components/AdditionalInfoData";
import NationalNoticeService from "services/NationalNoticeService";
import Tr from "translations/Translations";
import useSnackbar from "hooks/useSnackbar";
import iconWorksReducer from "libs/iconWorksReducer";
import scrollTo from "libs/scrollTo";
import NationalNoticeStatus from "../constants/NationalNoticeStatus";
import {isDemandModification} from "libs/getDemandStatus";
import GridBlock from "components/GridBlock";
import Button from "components/Button";
import Confirm from "components/popup/confirm/Confirm";
import {makeStyles} from "@material-ui/styles";
import RouterPath, {createPath} from "libs/RouterPath";
import BoldTypography from "components/BoldTypography";
import Section from "pages/purchaser/MyProcedure/scenes/part2/components/Section";
import FormTranslationContext from "context/FormTranslationContext";
import _ from "lodash";
import OffersData from "./components/OffersData";
import ContractorData from "./components/ContractorData";
import ContractData from "./components/ContractData";

function getKey(id) {
    const split = id.split(".").filter(x => !/^\d+$/.test(x));
    split[0] = 'tenderResultNotice';
    return split.join(".");
}

const useStyle = makeStyles(theme => ({
    collapse: {
        marginTop: theme.spacing(3)
    },
    info: {
        textAlign: "center",
        fontWeight: "bold",
        marginTop: theme.spacing(2),
        cursor: "pointer"
    }
}))

function ResultNoticeForm(
    {
        demandId,
        demandHeader
    }) {
    const classes = useStyle();
    const FORM_ID = "TenderResultNotice";
    const {enqueueSuccessSnackbar, enqueueErrorSnackbar} = useSnackbar();
    const [confirmRemove, setConfirmRemove] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [initData, setInitData] = useState(null);
    const [dictionaries, setDictionaries] = useState(null);
    const [translations, setTranslations] = useState(null);

    // eslint-disable-next-line
    const modification = useMemo(() => isDemandModification(), [demandHeader]); //usunac
    const [buttonWorks, dispatchButtonWorks] = useReducer(iconWorksReducer, {
        anyWorks: false,
        save: false,
        draft: false
    });

    const formTr = (id) => {
        const key = getKey(id);
        return translations.labels[key] || key;
    }
    const formTip = (id) => {
        const key = getKey(id);
        return translations.tips[key];
    }

    const getDict = (id) => {
        return dictionaries[id].items;
    }

    const getDynamicDict = (id) => {
        return initData.dictionaries?.[id]?.items || [];
    }

    const loadDynamicDictionaries = async () => {
        try {
            const resp = await NationalNoticeService.getTenderResultNoticeDynamicDictionaries(demandId);
            if(resp) {
                setInitData(x => ({...x, dictionaries: {...x.dictionaries, ...resp}}))
                return resp;
            }
        } catch (e) {
            enqueueErrorSnackbar(Tr("unknown_error_message"));
        }
    }

    useEffect(() => {
        Promise.all([
            NationalNoticeService.getTenderResultNotice(demandId),
            NationalNoticeService.getTenderResultNoticeDictionaries(demandId),
            NationalNoticeService.getTenderResultNoticeTranslations(demandId)])
            .then(values => {
                setInitData(values[0]);
                setDictionaries(values[1]);
                setTranslations(values[2]);
            }).then(() => setLoaded(true))


    }, [demandId])

    const handleSave = (formikProps) => {
        formikProps.setFieldValue("_editable", false);
        dispatchButtonWorks({type: 'draft', works: true});
        NationalNoticeService.saveTenderResultNoticeDraft(demandId, formikProps.values)
            .then((resp) => {
                if (!!resp) {
                    enqueueSuccessSnackbar(Tr("saved_draft"));
                    setInitData(x => ({...x, ...resp}))
                } else {
                    throw new Error();
                }
            })
            .catch(() => enqueueErrorSnackbar(Tr("unknown_error_message")))
            .finally(() => {
                formikProps.setFieldValue("_editable", true);
                dispatchButtonWorks({type: 'draft', works: false});
            })
    }

    const handleSubmit = (formikProps) => {
        (async () => {
            try {
                const casted2 = tenderResultNoticeValidationCast.cast(formikProps.values);
                const casted = await tenderResultNoticeValidation().validate(casted2, {abortEarly: false, strict: true})
                //const casted = contractNoticeValidation().cast(formikProps.values);
                formikProps.setFieldValue("_editable", false)
                dispatchButtonWorks({type: 'save', works: true});
                NationalNoticeService.saveTenderResultNotice(demandId, casted)
                    .then((resp) => {
                        if (resp.success) {
                            enqueueSuccessSnackbar(Tr("saved"))
                            setInitData(x => ({...x, ...resp.result}))
                        } else {
                            const keys = Object.keys(resp.errors);
                            let message;
                            if(Array.isArray()){
                                message = resp.errors[keys[0]][0];
                            } else if(_.isString(resp.errors[keys[0]])) {
                                message = resp.errors[keys[0]];
                            } else {
                                message = Tr("unknown_error_message")
                            }

                            navigateToError(keys[0], message, formikProps, true)
                        }
                    })
                    .catch(() => enqueueErrorSnackbar(Tr("unknown_error_message")))
                    .finally(() => {
                        formikProps.setFieldValue("_editable", true);
                        dispatchButtonWorks({type: 'save', works: false});
                    })
            } catch (e) {
                console.log(e);
                if (e.inner && e.inner.length > 0) {
                    e.inner.forEach((error) => {
                        const formikPath = error.path.replaceAll("[", ".").replaceAll("]", "");
                        formikProps.setFieldTouched(formikPath, true);
                        formikProps.setFieldError(formikPath, error.message);
                    });
                    navigateToError(e.inner[0].path, e.inner[0].message, formikProps, false);
                }
            }
        })()
    }

    const navigateToError = (errorpath, message, formikProps, setError) => {
        const formikPath = errorpath.replaceAll("[", ".").replaceAll("]", "");
        if (setError) {
            formikProps.setFieldTouched(formikPath, true);
            formikProps.setFieldError(formikPath, message);
        }

        const path = errorpath.split(".");
        enqueueErrorSnackbar(<span><strong>{message}</strong>{": " + formTr(path.map(x => x.replace(/\[(.*)\]/g, '')).join("."))}</span>)
        formikProps.setFieldValue("step", path[1]);
        if (path.length > 2) {
            if (path[1] === "orderObject" && path[2].startsWith("divided[")) {
                formikProps.setFieldValue("orderDealing", parseInt(path[2].replace(/\D/g, '')));
            } else if (path[1] === "orderDetails" && path[2].startsWith("primaryLots[")) {
                formikProps.setFieldValue("primaryDealing", parseInt(path[2].replace(/\D/g, '')));
            } else if (path[1] === "orderDetails" && path[2].startsWith("partnershipLots[")) {
                formikProps.setFieldValue("partnershipDealing", parseInt(path[2].replace(/\D/g, '')));
            } else if (path[1] === "tenderCompletionData" && path[2].startsWith("divided[")) {
                formikProps.setFieldValue("completionDealing", parseInt(path[2].replace(/\D/g, '')));
            } else if (path[1] === "offersData" && path[2].startsWith("divided[")) {
                formikProps.setFieldValue("offersDealing", parseInt(path[2].replace(/\D/g, '')));
            } else if (path[1] === "contractorData" && path[2].startsWith("divided[")) {
                formikProps.setFieldValue("contractorDealing", parseInt(path[2].replace(/\D/g, '')));
            } else if (path[1] === "contractData" && path[2].startsWith("divided[")) {
                formikProps.setFieldValue("contractDealing", parseInt(path[2].replace(/\D/g, '')));
            }
        }
        setTimeout((p) => scrollTo('[data-id="' + p + '"]', 100), 100, formikPath)
    }

    const createNewVersion = () => {
        setLoaded(false);
        setInitData(null);
        NationalNoticeService.createNewTenderResultNoticeVersion(demandId).then((resp) => setInitData(resp)).then(() => setLoaded(true));
    }

    const deleteLastVersion = () => {
        setConfirmRemove(false)
        setLoaded(false);
        setInitData(null);

        (async () => {
            try {
                await NationalNoticeService.deleteLastTenderResultNoticeVersion(demandId);
                const resp = await NationalNoticeService.getTenderResultNotice(demandId);
                setInitData(resp);
            } catch (e) {
            }
            setLoaded(true);
        })();
    }

    if (!loaded) return <Loading/>

    return (
            <Section  title={Tr(!initData.updateNotice ? "bzp_tender_result_notice" : "bzp_notice_update")}>
                <FormTranslationContext.Provider value={{
                    formTr, formTip, getDict, getDynamicDict, loadDynamicDictionaries: initData.canLoadDynamicDictionaries ? loadDynamicDictionaries : undefined
                }}>
                    <Form
                        initialValues={{
                            _editable: initData.editable && (initData.status === NationalNoticeStatus.DRAFT || initData.status === NationalNoticeStatus.COMPLETED),
                            _passive: initData.passive,
                            _onlyNotice: initData.onlyNotice,
                            _updateNotice: initData.updateNotice,
                            form: {
                                basicData: initData.form.basicData || {},
                                purchaserData: initData.form.purchaserData || {},
                                realizationType: initData.form.realizationType || {},
                                orderObject: initData.form.orderObject || {},
                                tenderCompletionData: initData.form.tenderCompletionData || {},
                                offersData: initData.form.offersData || {},
                                contractorData: initData.form.contractorData || {},
                                contractData: initData.form.contractData || {},
                                additionalInfo: initData.form.additionalInfo || {},
                            },
                            step: 'basicData',
                            orderDealing: -1,
                            completionDealing: -1,
                            offersDealing: -1,
                            contractorDealing: -1,
                            contractDealing: -1
                        }}
                        validationSchema={tenderResultNoticeValidation()}
                        onSubmit={() => {
                        }}
                        id={FORM_ID}
                        validateOnChange={false}
                        validateOnBlur={false}
                    >
                        {
                            (formikProps) => {
                                const step = formikProps.values.step
                                return (<>
                                    {!!initData.ezamowieniaErrors && <>
                                        <BoldTypography>{Tr('bzp_last_ezamowienia_errors')}</BoldTypography>
                                        <ul>
                                            {
                                                Object.keys(initData.ezamowieniaErrors).map(x => (<ul key={x}>
                                                    <li>{x}
                                                        <ul>
                                                            {initData.ezamowieniaErrors[x].map(e => (<li key={e}>{e}</li>))}
                                                        </ul>
                                                    </li>
                                                </ul>))
                                            }
                                        </ul>
                                    </>}

                                    <Typography id="form-top-scroll-to" variant="body2"
                                                align='right'><strong>{Tr('current_state')}:</strong> {Tr("ezamowienia_state_" + initData.status.toLowerCase())}
                                    </Typography>
                                    <FormNavigator
                                        formikProps={formikProps}
                                        id="step"
                                        items={tenderResultNoticeStepsOrder}
                                        formTr={formTr}
                                        trPrefix="tenderResultNotice."
                                        onSubmit={() => handleSubmit(formikProps)}
                                        onSave={() => handleSave(formikProps)}
                                        onCreateNewVersion={initData.editable && initData.status === NationalNoticeStatus.PUBLISHED ? createNewVersion : undefined}
                                        buttonWorks={buttonWorks}
                                        doAutoSave={initData.editable && (initData.status === NationalNoticeStatus.DRAFT || initData.status === NationalNoticeStatus.COMPLETED)}
                                    />

                                    <Collapse className={classes.collapse} in={step === "basicData"} unmountOnExit>
                                        <BasicData formikProps={formikProps} id="form.basicData" tenderPlanDisabled={demandHeader && (demandHeader.hasPlanItems || demandHeader.hasPurchaseRequests)}/>
                                    </Collapse>
                                    <Collapse className={classes.collapse} in={step === "purchaserData"} unmountOnExit>
                                        <PurchaserData formikProps={formikProps} id="form.purchaserData"/>
                                    </Collapse>
                                    <Collapse className={classes.collapse} in={step === "realizationType"} unmountOnExit>
                                        <RealizationTypeData formikProps={formikProps} id="form.realizationType"/>
                                    </Collapse>
                                    <Collapse className={classes.collapse} in={step === "orderObject"} unmountOnExit>
                                        <OrderObjectData formikProps={formikProps} id="form.orderObject"/>
                                    </Collapse>
                                    <Collapse className={classes.collapse} in={step === "tenderCompletionData"} unmountOnExit>
                                        <TenderCompletionData formikProps={formikProps} id="form.tenderCompletionData"/>
                                    </Collapse>
                                    <Collapse className={classes.collapse} in={step === "offersData"} unmountOnExit>
                                        <OffersData formikProps={formikProps} id="form.offersData"/>
                                    </Collapse>
                                    <Collapse className={classes.collapse} in={step === "contractorData"} unmountOnExit>
                                        <ContractorData formikProps={formikProps} id="form.contractorData"/>
                                    </Collapse>
                                    <Collapse className={classes.collapse} in={step === "contractData"} unmountOnExit>
                                        <ContractData formikProps={formikProps} id="form.contractData"/>
                                    </Collapse>
                                    <Collapse className={classes.collapse} in={step === "additionalInfo"} unmountOnExit>
                                        <AdditionalInfoData formikProps={formikProps} id="form.additionalInfo"/>
                                    </Collapse>

                                    <GridBlock justify="flex-end">
                                        {initData.editable && initData.modifiedDate != null && (initData.status === NationalNoticeStatus.DRAFT || initData.status === NationalNoticeStatus.COMPLETED) &&
                                            <Button variant="outlined" onClick={() => setConfirmRemove(true)}>{Tr("bzp_remove_last_version")}</Button>
                                        }
                                    </GridBlock>
                                    {initData.status === NationalNoticeStatus.COMPLETED &&
                                        <Typography
                                            className={classes.info}>{Tr("bzp_go_to_result_notice_info")}
                                            <Button variant="text"
                                                    onClick={() => RouterPath.redirect(createPath(RouterPath.myProcedure.steps.MP_4_ID, {id: demandId}))}>
                                                {Tr("mp_4")}
                                            </Button> </Typography>}


                                    <Confirm
                                        confirmText='bzp_remove_last_version_confirm'
                                        onCancel={() => setConfirmRemove(false)}
                                        onConfirm={deleteLastVersion}
                                        open={confirmRemove}
                                    />
                                </>)
                            }
                        }
                    </Form>
                </FormTranslationContext.Provider>
            </Section>
    )
}

ResultNoticeForm.propTypes = {
    demandId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
};

export default connect((state) => ({
    demandHeader: state.resources.demandHeader
}), {})(ResultNoticeForm);
